import { SecretField } from "@leylion/design-system";
import type {
  ClipboardEvent,
  FormEvent,
  KeyboardEvent,
  MutableRefObject
} from "react";
import type { Language, SignupText } from "./signup-copy";

export function SignupAccountStep({
  language,
  text,
  email,
  ownerName,
  businessName,
  password,
  accepted,
  error,
  busy,
  onEmailChange,
  onOwnerNameChange,
  onBusinessNameChange,
  onPasswordChange,
  onAcceptedChange,
  onSubmit,
  onSignIn
}: {
  language: Language;
  text: SignupText;
  email: string;
  ownerName: string;
  businessName: string;
  password: string;
  accepted: boolean;
  error: string;
  busy: boolean;
  onEmailChange: (value: string) => void;
  onOwnerNameChange: (value: string) => void;
  onBusinessNameChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onAcceptedChange: (value: boolean) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onSignIn: () => void;
}) {
  return (
    <section>
      <div className="ss-step"><span>{text.accountStep}</span><div className="ss-bar"><span style={{ width: "50%" }} /></div><span>{text.onlyTwoSteps}</span></div>
      <header className="ss-form-head"><span className="ss-form-kicker">{text.welcome}</span><h2>{text.startTitle}</h2><p>{text.startLead}</p></header>
      <form className="ss-grid" onSubmit={onSubmit} noValidate>
        <label className="ss-field"><span className="ss-label">{text.email}</span><span className="ss-input"><input data-testid="public-signup-email" dir="ltr" type="email" autoComplete="email" placeholder={text.emailPlaceholder} value={email} onChange={(event) => onEmailChange(event.target.value)} /></span></label>
        <label className="ss-field"><span className="ss-label">{text.owner}</span><span className="ss-input"><input data-testid="public-signup-owner" autoComplete="name" placeholder={text.ownerPlaceholder} value={ownerName} onChange={(event) => onOwnerNameChange(event.target.value)} /></span></label>
        <label className="ss-field"><span className="ss-label">{text.business}</span><span className="ss-input"><input data-testid="public-signup-business" autoComplete="organization" placeholder={text.businessPlaceholder} value={businessName} onChange={(event) => onBusinessNameChange(event.target.value)} /></span></label>
        <SecretField
          className="ss-field ss-secret-field"
          data-testid="public-signup-password"
          label={text.password}
          revealLabel={language === "ar" ? "إظهار كلمة المرور" : "Show password"}
          concealLabel={language === "ar" ? "إخفاء كلمة المرور" : "Hide password"}
          hint={text.passwordHint}
          autoComplete="new-password"
          placeholder={text.passwordPlaceholder}
          value={password}
          onChange={(event) => onPasswordChange(event.target.value)}
        />
        <label className="ss-consent"><input data-testid="public-signup-terms" type="checkbox" checked={accepted} onChange={(event) => onAcceptedChange(event.target.checked)} /><span>{text.termsPrefix} <a href="/legal/terms-v1.html" target="_blank" rel="noreferrer">{text.terms}</a> {text.termsAnd} <a href="/legal/privacy-v1.html" target="_blank" rel="noreferrer">{text.privacy}</a>.</span></label>
        {error ? <div className="ss-form-error" role="alert">{error}</div> : null}
        <button className="ss-btn ss-primary" data-testid="public-signup-submit" type="submit" disabled={busy}>{busy ? "…" : text.start}</button>
        <div className="ss-divider"><span>{text.already}</span></div>
        <button className="ss-btn ss-secondary" type="button" onClick={onSignIn}>{text.signIn}</button>
      </form>
    </section>
  );
}

export function SignupVerificationStep({
  text,
  email,
  verificationCode,
  codeRefs,
  error,
  notice,
  busy,
  resendSeconds,
  onSubmit,
  onCodeChange,
  onCodeKeyDown,
  onCodePaste,
  onResend,
  onBack
}: {
  text: SignupText;
  email: string;
  verificationCode: string[];
  codeRefs: MutableRefObject<Array<HTMLInputElement | null>>;
  error: string;
  notice: string;
  busy: boolean;
  resendSeconds: number;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onCodeChange: (index: number, value: string) => void;
  onCodeKeyDown: (index: number, event: KeyboardEvent<HTMLInputElement>) => void;
  onCodePaste: (event: ClipboardEvent<HTMLDivElement>) => void;
  onResend: () => void;
  onBack: () => void;
}) {
  return (
    <section>
      <div className="ss-step"><span>{text.verifyStep}</span><div className="ss-bar"><span style={{ width: "100%" }} /></div><span>{text.finalStep}</span></div>
      <header className="ss-form-head"><span className="ss-form-kicker">{text.secure}</span><h2>{text.verifyTitle}</h2><p>{text.verifyLead}</p></header>
      <div className="ss-verify-mail"><bdi dir="ltr">{email.trim()}</bdi></div>
      <form className="ss-grid" onSubmit={onSubmit}>
        <div className="ss-code" onPaste={onCodePaste}>
          {verificationCode.map((digit, index) => (
            <input
              key={index}
              ref={(element) => { codeRefs.current[index] = element; }}
              data-testid={`public-signup-code-${index}`}
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              aria-label={`${index + 1}`}
              maxLength={1}
              value={digit}
              onChange={(event) => onCodeChange(index, event.target.value)}
              onKeyDown={(event) => onCodeKeyDown(index, event)}
            />
          ))}
        </div>
        {notice ? <div className="ss-form-notice" role="status">{notice}</div> : null}
        {error ? <div className="ss-form-error" role="alert">{error}</div> : null}
        <button className="ss-btn ss-primary" data-testid="public-signup-verify" type="submit" disabled={busy}>{busy ? "…" : text.verify}</button>
        <div className="ss-resend-row"><span>{text.noCode}</span><button data-testid="public-signup-resend" type="button" disabled={busy || resendSeconds > 0} onClick={onResend}>{resendSeconds > 0 ? `${text.resendIn} ${resendSeconds}${text.seconds}` : text.resend}</button></div>
        <button className="ss-btn ss-ghost" type="button" disabled={busy} onClick={onBack}>{text.back}</button>
      </form>
    </section>
  );
}
